import { FC, ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { Fade } from "@mui/material";
import { FormikErrors } from "formik";
import {
  ModalButton,
  CloseModalButton,
  BackModalButton,
} from "components/Buttons";
import { TextInput, PhoneInput } from "components/Inputs";
import { AppText } from "components";
import { ModalHeading, Form, InputContainer } from "../styled";
import { ReactComponent as PhoneIcon } from "assets/icons/phone.svg";

interface MainChangePhoneStepProps {
  oldNumber: string;
  isSubmitting: boolean;
  values: { [field: string]: any };
  onClose: () => void;
  errors: { [field: string]: string };
  handleChange: (e: ChangeEvent<any>) => void;
  handleSubmit: () => void;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean
  ) =>
    | Promise<void>
    | Promise<
        FormikErrors<{
          newPhoneNumber: string;
          phonePref: string;
        }>
      >;
  setFieldTouched: (
    field: string,
    touched?: boolean,
    shouldValidate?: boolean
  ) =>
    | Promise<void>
    | Promise<
        FormikErrors<{
          newPhoneNumber: string;
          phonePref: string;
        }>
      >;
  handlePhonePrefChange: (e: string) => void;
  touched: { [field: string]: boolean };
}

const MainChangePhoneStep: FC<MainChangePhoneStepProps> = ({
  oldNumber,
  onClose,
  isSubmitting,
  handleSubmit,
  values,
  errors,
  touched,
  handleChange,
  setFieldValue,
  setFieldTouched,
  handlePhonePrefChange,
}) => {
  const { t } = useTranslation();

  return (
    <Fade in timeout={500}>
      <Form onSubmit={handleSubmit}>
        <ModalHeading $centered $capitalized>
          {t("CHANGE_PHONE_NUMBER")}
        </ModalHeading>
        <CloseModalButton onClose={onClose} />
        <BackModalButton onBack={onClose} />

        <InputContainer style={{ marginTop: "1rem" }}>
          <AppText>{t("CURRENT_PHONE_NUMBER")}</AppText>
          <TextInput disabled value={oldNumber} leftIcon={<PhoneIcon />} />
        </InputContainer>

        <InputContainer>
          <AppText>{t("NEW_PHONE_NUMBER")}</AppText>
          <PhoneInput
            name="newPhoneNumber"
            placeholder={t("ENTER_NEW_PHONE_NUMBER")}
            value={values.newPhoneNumber}
            setValue={(value) => setFieldValue("newPhoneNumber", value)}
            onBlur={() => setFieldTouched("newPhoneNumber")}
            error={touched.newPhoneNumber && Boolean(errors.newPhoneNumber)}
            helperText={
              touched.newPhoneNumber && t(errors.newPhoneNumber || "")
            }
            phonePref={values.phonePref}
            setPref={handlePhonePrefChange}
          />
        </InputContainer>

        <ModalButton
          variant="contained"
          fullWidth
          type="submit"
          style={{ marginTop: "2.8rem" }}
          disabled={isSubmitting}
        >
          {t("CHANGE")}
        </ModalButton>
      </Form>
    </Fade>
  );
};

export default MainChangePhoneStep;
